import React, { useState, useEffect } from "react";
import CustomerTableWrapper from "src/Components/Customer/CustomerTableWrapper";
import PageHeader from "src/Components/Customer/PageHeader";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import httpRequest from "src/helpers/httpRequest";
import greenLogo from "src/assets/images/shifara-green-logo.png";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import BackIcon from "src/assets/images/back-icon.png";
import { usePDF, Margin } from "react-to-pdf";
import { truncateString } from "src/helpers/helperFunctions";
import AddCartComponent from "src/Components/Customer/ProductsAndServicesComponents/AddCartComponent";
import greenIcon from "src/assets/images/cart-icon-green.png";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { addItem } from "src/Store/cartSlice";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faDownload } from "@fortawesome/free-solid-svg-icons";
import { capitalize } from "@mui/material";

type tabKeys = "pharmacy" | "diagnostic" | "homecare";
interface IPrescribedResponse {
  [key: string]: any;
}

interface IPrescribedItem {
  IPrescribedItem?: string;
  [key: string]: any;
}

// New Modal Component that matches the image exactly
const PrescriptionModal: React.FC<{
  onClose: () => void;
  prescribedData: IPrescribedItem;
  productType: string;
  columnWidths: string;
  isLoader: boolean;
}> = ({ onClose, prescribedData, productType, columnWidths, isLoader }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { toPDF, targetRef } = usePDF({
    filename: "prescription.pdf",
    page: {
      margin: Margin.SMALL,
    },
  });

  const handleDownload = async () => {
    setIsDownloading(true);
    let toastId = toast("Downloading...", {
      type: "info",
      isLoading: true,
      autoClose: false,
    });
    await toPDF();
    toast.update(toastId, {
      type: "success",
      isLoading: false,
      autoClose: 1000,
      render: "Downloaded",
    });
    setIsDownloading(false);
  };

  return (
    <div className="lg:p-[1vw] p-4">
      <div ref={targetRef} className="space-y-3 ">
        {/* Header Section */}
        <img src={greenLogo} alt="Shifara" className="w-[10vw] min-w-[100px]" />
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {/* Lorem ipsum text */}
            <p
              className={`text-[#6D6D6D] md:w-[80%] font-size-15px font-Poppins-Regular leading-relaxed ${
                isDownloading ? "mt-[-5px]" : ""
              }`}
            >
              Dedicated to improving patient well-being, Dr. {capitalize(prescribedData?.doctorDetails?.firstName || "")} {capitalize(prescribedData?.doctorDetails?.lastName || "")} Castor offers trusted medical guidance and personalized treatment, ensuring every patient receives the highest quality care.
            </p>
          </div>

          {/* Date Information */}
          <div className="text-right space-y-1">
            <p className="text-[#515151] font-size-15px font-Poppins-Regular">
              <strong>Date Created :</strong>{" "}
              {moment(prescribedData?.createdAt).format("MMM DD, YYYY")}
            </p>
          </div>
        </div>

        {/* Patient Information */}
        <div className="space-y-2 border-b border-[#ccc] py-2 border-t">
          <div className="flex gap-1.5">
            <strong className="text-[#515151] font-size-15px font-Poppins-Regular">
              Name:
            </strong>
            <span className="text-[#727272] font-size-15px font-Poppins-Regular">
              {prescribedData?.customerDetails?.firstName +
                " " +
                prescribedData?.customerDetails?.lastName}
            </span>
          </div>
          <div className="flex gap-1.5">
            <strong className="text-[#515151] font-size-15px font-Poppins-Regular">
              Age/Gender:
            </strong>
            <span className="text-[#727272] font-size-15px font-Poppins-Regular">
              {moment().diff(prescribedData?.customerDetails?.age, "years")}{" "}
              Years/{capitalize(prescribedData?.customerDetails?.gender || "")}
            </span>
          </div>
        </div>

        <div>
          <h2 className="text-[#212121] mb-3 font-size-18px font-Poppins-Medium">
            {productType === "pharmacy"
              ? "Medicines"
              : productType === "diagnostic"
              ? "Tests"
              : "Services"}
          </h2>

          {/* Test Section */}
          <CustomerTableWrapper
            isLoading={isLoader}
            thead={
              productType === "pharmacy"
                ? ["S.No", "Medicine", "Frequency", "Dosage", "Instructions"]
                : productType === "diagnostic"
                ? ["S.No", "Tests", "Instructions"]
                : ["S.No", "Services", "Instructions"]
            }
            page={1}
            total={1}
            columnWidth={columnWidths}
          >
            {prescribedData?.prescribeDetails &&
              prescribedData?.prescribeDetails.map(
                (item: IPrescribedItem, index: number) => (
                  <ul
                    className={` font-size-14px tbody-light-table tbody-light-table-customer customer-table-wrapper-body-padding-non customer-table-wrapper-body font-Poppins-Regular grid`}
                    style={{ gridTemplateColumns: columnWidths }}
                  >
                    <li className="action-last-colum-non">{index + 1}</li>
                    <li>{item?.productName}</li>
                    {productType === "pharmacy" && (
                      <>
                        <li className="">{item?.frequency}</li>
                        <li>{item?.dosage}</li>
                      </>
                    )}
                    <li title={item?.instruction}>
                      {truncateString(item?.instruction, 120)}
                    </li>
                  </ul>
                )
              )}
          </CustomerTableWrapper>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handleDownload}
          className="border-[#00756e] border text-[#00756e] px-6 py-2 rounded-full font-size-15px font-Poppins-Medium flex items-center gap-1 hover:bg-[#00756e] hover:text-white transition-colors"
        >
          <FontAwesomeIcon icon={faDownload} className="font-size-14px" />
          Download
        </button>
      </div>
    </div>
  );
};

const PrescribedDetails: React.FC<{ id?: string }> = ({ id = "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showModal, setShowModal] = useState(false);
  const [productType, setProductType] = useState<string>("diagnostic");
  let pharmacyColumnWidths = "10% 18.5% 19% 15% 27.5% 10%";
  let diagnosticColumnWidths = "10% 35% 45% 10%";

  const navigate = useNavigate();
  const { toPDF, targetRef } = usePDF({
    filename: "prescription.pdf",
    page: {
      margin: Margin.SMALL,
    },
  });

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [prescribedData, setPrescribedData] = useState<IPrescribedItem>([]);

  const handleDownload = () => {
    toPDF();
  };

  const addToCartInBulk = () => {
    if (productType === "pharmacy") {
      (prescribedData?.prescribeDetails || []).map(
        (item: { productId: string }) => {
          dispatch(
            addItem({
              category: "pharmacyProducts",
              product: { _id: item?.productId, quantity: 1 },
            })
          );
        }
      );
    } else if (productType === "diagnostic") {
      (prescribedData?.prescribeDetails || []).map(
        (item: { productId: string }) => {
          dispatch(
            addItem({
              category: "diagnosticProducts",
              product: { _id: item?.productId, quantity: 1 },
            })
          );
        }
      );
    }
  };

  const FetchPrescibedData = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IPrescribedResponse>({
      path: `/appoiment/view-prescription?prescribeId=${id}`,
    });
    if (res) {
      const { document = [], paginated = { currentPage: 1, totalPages: 1 } } =
        res;
      setPrescribedData(document);
      setProductType(document?.productType || "diagnostic");
    } else {
      toast(err, { type: "error" }); // Show error toast if API call fails
    }
    setIsLoader(false);
  };

  const readRedDot = async () => {
    const { res, err } = await httpRequest<any>({
      path: `/appoiment/read?prescribeId=${id}&query=prescribe`,
    });
    if (res) {
      console.log(res, "response");
    } else {
      console.log(err?.message, "response");
    }
  };

  useEffect(() => {
    if (id && productType) {
      FetchPrescibedData();
      readRedDot();
    }
  }, [id, productType]);

  const BackButton: React.FC = () => {
    return (
      <button className="right-bar-icon" onClick={() => navigate(-1)}>
        <img src={BackIcon} alt="back-icon" />
      </button>
    );
  }; // display back icon in page header

  return (
    <>
      {prescribedData && (
        <PrescriptionModal
          onClose={() => setShowModal(false)}
          prescribedData={prescribedData}
          productType={productType}
          columnWidths={
            productType === "pharmacy"
              ? pharmacyColumnWidths
              : productType === "diagnostic"
              ? diagnosticColumnWidths
              : diagnosticColumnWidths
          }
          isLoader={isLoader}
        />
      )}
    </>
  );
};

export default PrescribedDetails;
